import { Banknote, ChevronDown, Landmark, Smartphone } from "lucide-react";
import { useState } from "react";

import type { Account, Provider } from "./types";
import { money } from "./utils";

function fallbackIcon(kind?: string) {
  if (kind === "MOBILE_MONEY") return Smartphone;
  if (kind === "BANK") return Landmark;
  return Banknote;
}

export function FinancialLogo({ src, name, kind, compact=false }: { src?: string; name: string; kind?: string; compact?: boolean }) {
  const Icon = fallbackIcon(kind);
  const box = compact ? "h-7 w-16" : "h-9 w-20";
  return <span className={`grid ${box} shrink-0 place-items-center overflow-hidden rounded border border-slate-200 bg-white`}>
    {src ? <img src={src} alt="" className="max-h-full max-w-full object-contain" /> : <Icon size={compact?15:17} className="text-slate-500"/>}
  </span>;
}

export function AccountPicker({
  accounts, value, onChange, required=false, label="Compte d’encaissement"
}: {
  accounts: Account[]; value: string; onChange: (value:string)=>void; required?: boolean; label?: string;
}) {
  const [open,setOpen]=useState(false);
  const selected=accounts.find((item)=>item.id===Number(value));
  return <label className="relative block">
    <span className="label">{label}</span>
    <button type="button" aria-haspopup="listbox" aria-expanded={open} aria-required={required} onClick={()=>setOpen((v)=>!v)} className="field flex items-center justify-between gap-2 text-left">
      {selected ? <span className="flex min-w-0 items-center gap-2"><FinancialLogo src={selected.provider_logo} name={selected.provider_name||selected.nom} kind={selected.type} compact/><span className="min-w-0"><span className="block truncate font-semibold text-slate-900">{selected.nom}</span><span className="block truncate text-[11px] text-slate-400">{selected.provider_name||selected.type} · {money(selected.solde_actuel)}</span></span></span> : <span className="text-slate-400">Choisir un compte…</span>}
      <ChevronDown size={15} className="shrink-0 text-slate-400"/>
    </button>
    {open&&<div role="listbox" className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border border-slate-200 bg-white p-1 shadow-lg">
      {accounts.map((item)=><button type="button" role="option" aria-selected={item.id===Number(value)} key={item.id} onClick={()=>{onChange(String(item.id));setOpen(false);}} className="flex w-full items-center gap-2 rounded px-2 py-2 text-left hover:bg-slate-50">
        <FinancialLogo src={item.provider_logo} name={item.provider_name||item.nom} kind={item.type} compact/>
        <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{item.nom}</span><span className="block truncate text-[11px] text-slate-400">{item.provider_name||item.type} · {money(item.solde_actuel)}</span></span>
      </button>)}
      {!accounts.length&&<div className="px-3 py-3 text-xs text-slate-500">Aucun compte actif. Créez d’abord un compte dans Comptes / Caisse.</div>}
    </div>}
  </label>;
}

export function ProviderPicker({
  providers, value, onChange
}: {
  providers: Provider[]; value: string; onChange: (value:string)=>void;
}) {
  return <div>
    <div className="label">Fournisseur financier</div>
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {providers.map((item)=><button type="button" key={item.id} onClick={()=>onChange(String(item.id))} className={`flex min-h-12 items-center gap-2 rounded-md border px-2.5 py-2 text-left transition ${String(item.id)===value?"border-slate-950 bg-slate-50":"border-slate-200 bg-white hover:bg-slate-50"}`}>
        <FinancialLogo src={item.logo} name={item.name} kind={item.kind} compact/>
        <span className="min-w-0"><span className="block truncate text-xs font-bold text-slate-900">{item.name}</span><span className="block truncate text-[10px] text-slate-400">{item.kind.replaceAll("_"," ")}</span></span>
      </button>)}
    </div>
    {!providers.length&&<p className="mt-2 text-xs text-red-600">Aucun fournisseur financier disponible pour ce pays.</p>}
  </div>;
}


export function AccountChoiceGrid({
  accounts, value, onChange, label="Compte"
}: {
  accounts: Account[]; value: string; onChange: (value:string)=>void; label?: string;
}) {
  return <div>
    <div className="label">{label}</div>
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {accounts.map((item)=>{
        const selected=String(item.id)===value;
        return <button type="button" key={item.id} aria-pressed={selected} onClick={()=>onChange(String(item.id))} className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-left transition ${selected?"border-slate-950 bg-slate-50 ring-1 ring-slate-950":"border-slate-200 bg-white hover:bg-slate-50"}`}>
          <FinancialLogo src={item.provider_logo} name={item.provider_name||item.nom} kind={item.type}/>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-bold text-slate-900">{item.nom}</span>
            <span className="block truncate text-[10px] text-slate-500">{item.provider_name||item.type}{item.identifiant?` · ${item.identifiant}`:""}</span>
            <span className="mt-0.5 block text-[11px] font-semibold text-slate-700">{money(item.solde_actuel)}</span>
          </span>
        </button>;
      })}
    </div>
  </div>;
}
